/**
 * Centralised SEO metadata for every public route in CXOORA.
 *
 * Each entry follows the contract of <SEO />:
 *   { title, description, keywords?, image?, jsonLd? }
 *
 * Keep titles ≤ 60 chars and descriptions ≤ 160 chars for optimal SERP display.
 * Italian-first (default), English is served via the same URL + ?lang=en (handled by i18n).
 */

import { breadcrumbList, faqPage } from "@/components/SEO";

// Shared baseline keywords for the Italian B2B fractional-management vertical.
const CORE_KW = "fractional manager, manager frazionale, temporary manager italia, CFO frazionale, CMO frazionale, COO frazionale, manager a chiamata, manager a tempo parziale, consulente direzionale, interim management";

export const SEO_HOME = {
  title: "CXOORA — Fractional Manager per PMI Italiane | CFO, CMO, COO",
  description: "Trova il manager fractional verificato (CFO, CMO, COO, CRO) che ti serve. Check-up AI gratuito in 60 secondi e shortlist personalizzata in 48h.",
  keywords: `${CORE_KW}, piattaforma fractional, marketplace manager, PMI italia`,
  jsonLd: [
    breadcrumbList([{ name: "Home", path: "/" }]),
  ],
};

export const SEO_WHY = {
  title: "Perché CXOORA — Il Vantaggio del Fractional Management",
  description: "Scopri perché oltre 200 PMI italiane scelgono CXOORA per accedere a manager senior senza i costi e i tempi delle assunzioni tradizionali.",
  keywords: `${CORE_KW}, vantaggi fractional, perché temporary management`,
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Perché CXOORA", path: "/perche-cxoora" }])],
};

export const SEO_SOLUTIONS = {
  title: "Soluzioni di Fractional Management per ogni Sfida Aziendale",
  description: "Crescita, ristrutturazione, turnaround, digital transformation: trova il manager fractional specializzato sulla tua sfida specifica.",
  keywords: `${CORE_KW}, soluzioni manageriali, turnaround manager, change manager, transformation manager`,
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Soluzioni", path: "/soluzioni" }])],
};

export const SEO_HOW_IT_WORKS = {
  title: "Come Funziona CXOORA — Dal Check-up al Manager in 48h",
  description: "Il processo CXOORA in 4 step: check-up AI gratuito, matching, shortlist verificata in 48h, attivazione e workspace condiviso.",
  keywords: `${CORE_KW}, come trovare un fractional manager, processo selezione manager`,
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Come funziona", path: "/come-funziona" }])],
};

export const SEO_FOR_COMPANIES = {
  title: "Per le Imprese — Trova un Fractional Manager Verificato",
  description: "PMI italiane: accedi alla leadership senior che ti serve, solo per il tempo che ti serve. Check-up AI, matching in 48h, fatturazione trasparente.",
  keywords: `${CORE_KW}, manager per PMI, soluzioni per imprese, leadership a chiamata`,
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Per le imprese", path: "/per-le-imprese" }])],
};

export const SEO_FOR_MANAGERS = {
  title: "Per i Manager — Diventa Fractional Manager Verificato CXOORA",
  description: "Sei un manager senior? Entra nel network CXOORA, ricevi progetti curati su misura e gestisci i tuoi clienti da una piattaforma unica.",
  keywords: "fractional manager italia, lavoro temporary manager, opportunità manager senior, consulente direzionale lavoro, interim manager freelance",
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Per i manager", path: "/per-i-manager" }])],
};

export const SEO_APPLY_MANAGER = {
  title: "Candidati come Fractional Manager — Network CXOORA",
  description: "Carica il tuo CV, le esperienze e le aree di copertura: il nostro team valuta la tua candidatura per il network CXOORA in 5 giorni lavorativi.",
  keywords: "candidatura manager, registrazione manager fractional, opportunità lavoro manager senior",
  jsonLd: [breadcrumbList([
    { name: "Home", path: "/" },
    { name: "Per i manager", path: "/per-i-manager" },
    { name: "Candidatura", path: "/per-i-manager/candidati" },
  ])],
};

export const SEO_CHECKUP_LIGHT = {
  title: "Check-up AI Gratuito — Diagnosi Aziendale in 60 Secondi",
  description: "Descrivi la tua sfida e l'AI ti restituisce diagnosi, 3 azioni concrete e il profilo manageriale ideale. Gratis, senza registrazione.",
  keywords: "check-up aziendale gratis, diagnosi azienda AI, analisi PMI gratuita, audit aziendale online, assessment management",
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Check-up AI", path: "/check-up-light" }])],
};

export const SEO_PRICING = {
  title: "Tariffe CXOORA — Marketplace e Agency Direct",
  description: "Modello ibrido trasparente: Marketplace per progetti spot (fee 15%) e Agency Direct per ingaggi premium (≥ €3.000). Nessun costo fisso nascosto.",
  keywords: `${CORE_KW}, costi fractional manager, tariffe manager temporaneo, prezzi consulenza direzionale`,
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Tariffe", path: "/pricing" }])],
};

export const SEO_MANAGER_NETWORK = {
  title: "Network Manager Fractional — Scopri i Top Professionisti CXOORA",
  description: "Esplora la directory pubblica dei manager fractional verificati: profili, settori, aree geografiche e specializzazioni.",
  keywords: `${CORE_KW}, directory manager, lista manager italia, profili fractional manager`,
  jsonLd: [breadcrumbList([{ name: "Home", path: "/" }, { name: "Network manager", path: "/manager-network" }])],
};

const FAQ_ITEMS = [
  {
    q: "Cos'è un fractional manager?",
    a: "Un fractional manager (o manager frazionale) è un dirigente senior che opera a tempo parziale o per progetti specifici, mettendo a disposizione la propria esperienza maturata in ruoli executive (CFO, CMO, COO, ecc.) senza i costi e i tempi di un'assunzione full-time.",
  },
  {
    q: "Quanto costa un fractional manager su CXOORA?",
    a: "Su CXOORA il costo dipende dal modello scelto: il Marketplace ha tariffe a giornata pubbliche dai €600 al giorno, mentre il modello Agency Direct (ingaggi ≥ €3.000) prevede preventivi personalizzati. Trasparenza totale, nessun costo nascosto.",
  },
  {
    q: "Quanto tempo serve per trovare il manager giusto?",
    a: "CXOORA invia una shortlist personalizzata di 3-5 manager verificati entro 48 ore dal completamento del check-up. L'attivazione del manager scelto avviene tipicamente entro 5-10 giorni.",
  },
  {
    q: "I manager CXOORA sono verificati?",
    a: "Sì. Ogni manager nel network CXOORA passa attraverso un processo di vetting che include verifica del CV, referenze di almeno 3 clienti precedenti, intervista con un partner CXOORA e validazione delle competenze su un caso reale.",
  },
  {
    q: "Per quali settori CXOORA fornisce fractional manager?",
    a: "CXOORA copre tutti i settori delle PMI italiane: manifatturiero, retail/e-commerce, ICT/software, servizi alle imprese, healthcare, food&beverage, logistica, energia, fashion, agritech e altri.",
  },
  {
    q: "Posso terminare l'incarico in qualsiasi momento?",
    a: "Sì. I contratti CXOORA prevedono clausole flessibili di uscita con preavviso minimo di 30 giorni. Nessun vincolo a lungo termine.",
  },
  {
    q: "Come funziona il check-up AI gratuito?",
    a: "Il check-up AI di CXOORA è uno strumento gratuito che in 60 secondi analizza la tua sfida aziendale (descritta liberamente più settore e dimensione) e restituisce una diagnosi, 3 azioni concrete e il profilo manageriale più adatto. Powered by Claude Sonnet 4.5.",
  },
];

export const SEO_FAQ = {
  title: "Domande Frequenti — Fractional Management Spiegato | CXOORA",
  description: "Risposte alle domande più comuni sul fractional management: costi, tempistiche, vetting manager, settori, contratti, terminazione anticipata.",
  keywords: `${CORE_KW}, faq fractional, cos'è temporary manager, quanto costa fractional CFO, come funziona`,
  jsonLd: [
    breadcrumbList([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }]),
    faqPage(FAQ_ITEMS),
  ],
};

export const SEO_LOGIN = {
  title: "Accedi a CXOORA",
  description: "Entra nella tua dashboard CXOORA. Imprese: gestisci progetti e shortlist. Manager: rispondi alle proposte e gestisci i clienti.",
  keywords: "login cxoora, accedi piattaforma manager",
};

export const SEO_REGISTER = {
  title: "Registrati su CXOORA — Imprese o Manager",
  description: "Crea il tuo account CXOORA in 30 secondi. Aziende: avvia il matching con i manager fractional. Manager: candidati al network.",
  keywords: "registrazione cxoora, sign up fractional manager, account piattaforma",
};

export const SEO_LEGAL_PRIVACY = {
  title: "Privacy Policy — CXOORA",
  description: "Informativa sul trattamento dei dati personali ai sensi del GDPR (Reg. UE 2016/679).",
};

export const SEO_LEGAL_TERMS = {
  title: "Termini e Condizioni — CXOORA",
  description: "Termini e condizioni di utilizzo della piattaforma CXOORA.",
};

export const SEO_LEGAL_COOKIE = {
  title: "Cookie Policy — CXOORA",
  description: "Informazioni sui cookie utilizzati dalla piattaforma CXOORA.",
};
